import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Fade,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  Divider,
  ListSubheader
} from '@mui/material';
import {
  AutoAwesome,
  Send,
  CheckCircle,
  ArrowBack,
  Construction,
  Refresh,
  AttachFile,
  Close,
  Star,
  LocationOn,
  Image,
  Delete,
  FolderOpen,
  Assignment
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import ContractorHireDocumentPreview from './ContractorHireDocumentPreview';
import UnifiedProjectWizard from '../projects/UnifiedProjectWizard';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand, border } from '../../theme/colors';

const TOTAL_STEPS = 4;

// Standard Zimbabwe construction inspection/audit stages
const CONSTRUCTION_STAGES = [
  {
    id: 'building_plans',
    name: 'Building Plans',
    purpose: 'Preparation and approval of architectural drawings by the Local Authority.',
    auditFocus: 'Plan compliance, Local Authority submission, regulatory requirements.',
    suggestion: 'Requesting quotes for the preparation and submission of building plans to the Local Authority. This includes architectural drawing preparation, compliance review with local building regulations, and handling the approval process with the relevant municipal authority.'
  },
  {
    id: 'siting_setting_out',
    name: 'Siting and Setting Out',
    purpose: 'Verify the building is correctly positioned on the stand per approved site plans and boundary lines.',
    auditFocus: 'Boundary checks, building line compliance.',
    suggestion: 'Requesting quotes for siting and setting-out works to verify the building is correctly positioned on the stand (plot) in accordance with approved site plans and boundary lines. Scope includes boundary checks, building line compliance verification, and pegging of the foundation layout.'
  },
  {
    id: 'trenches_foundation',
    name: 'Trenches / Foundation Level',
    purpose: 'Inspect depth, width, and stability of trenches before concrete is poured.',
    auditFocus: 'Reinforcing steel inspection and excavation depth to meet structural requirements.',
    suggestion: 'Requesting quotes for trench excavation and foundation-level works. Scope includes excavation to the required depth and width, placement of reinforcing steel where required, and ensuring all dimensions meet the structural engineer\'s specifications before concrete is poured.'
  },
  {
    id: 'concrete_substructure',
    name: 'Concrete Inspection (Substructure)',
    purpose: 'Inspection of foundation concrete and anti-termite treatment.',
    auditFocus: 'Quality and depth of the concrete base.',
    suggestion: 'Requesting quotes for substructure concrete works including foundation concrete pouring and anti-termite treatment. Scope includes concrete mix quality control, correct pouring depth, compaction, and anti-termite chemical treatment as required by the approved plans.'
  },
  {
    id: 'dpc_floor_slab',
    name: 'Damp Proof Course (DPC) and Floor Slab',
    purpose: 'Inspection of DPC and oversite concrete (floor slab) to prevent moisture ingress.',
    auditFocus: 'Proper placement of DPC, plumbing pipe installation, and compacted filling.',
    suggestion: 'Requesting quotes for the installation of the Damp Proof Course (DPC) and oversite concrete floor slab. Scope includes supply and laying of approved DPC membrane, installation of plumbing pipes below slab, compacted filling, and pouring of oversite concrete to the specified thickness and mix.'
  },
  {
    id: 'walling',
    name: 'Walling (Mid-construction)',
    purpose: 'Check wall construction at critical heights — bottom window level and top window level.',
    auditFocus: 'Wall thickness, structural integrity, and lintel installation.',
    suggestion: 'Requesting quotes for mid-construction walling works. Scope covers brick/block laying from DPC to window sill level and continuing to wall plate level, including installation of window and door lintels, maintaining correct wall thickness, and ensuring structural integrity throughout.'
  },
  {
    id: 'roof_structure',
    name: 'Roof Structure (Roof Carcass)',
    purpose: 'Inspection of roof trusses and timber framework before covering.',
    auditFocus: 'Bracing, truss anchoring, and adherence to structural design.',
    suggestion: 'Requesting quotes for the supply and erection of the roof structure (carcass) prior to sheeting or tiling. Scope includes fabrication or supply of roof trusses, installation, bracing, anchoring to wall plates, and all timber framework in accordance with the approved structural design.'
  },
  {
    id: 'drainage_sewerage',
    name: 'Drainage and Sewerage (Drain Test)',
    purpose: 'Test plumbing and drainage systems for leaks and proper flow before cover-up.',
    auditFocus: 'Pipe gradient, joint sealing, and pressure testing (drain open test).',
    suggestion: 'Requesting quotes for drainage and sewerage installation and testing. Scope includes laying of all drainage and sewer pipes at correct gradients, joint sealing, connection to the municipal sewer or septic system, and conducting a drain open test to confirm no leaks before trenches are backfilled.'
  },
  {
    id: 'final_inspection',
    name: 'Final Inspection (Occupancy Certificate)',
    purpose: 'Comprehensive inspection on completion to obtain an Occupancy Certificate.',
    auditFocus: 'Verification that all previous stages were approved, the house is safe for habitation, and it matches the approved drawings.',
    suggestion: 'Requesting quotes for completion of all outstanding building works to the standard required for the Final Inspection and issuance of an Occupancy Certificate. Scope includes all finishing works, remediation of any outstanding defects, and ensuring the completed building matches the approved drawings in all respects.'
  }
];

// Label map used in the "Your Answers" summary panel
const ANSWER_LABELS = {
  workType: 'Scope of work',
  location: 'Project location',
  timeline: 'Timeline',
  description: 'Work description'
};

/**
 * AIContractorHiringDialog
 * Conversational dialog for sending quote requests to contractors.
 *
 * Step 0 — Project Association: pick an existing project (auto-selects if only one)
 * Step 1 — Scope Selection: entire project / specific stage / specific task
 * Step 2 — Timeline
 * Step 3 — Description
 *
 * Props:
 *   open        — boolean
 *   onClose     — callback
 *   onComplete  — callback({ contractors, answers })
 *   contractorId — optional; when set the contractor selection step is skipped
 *                  and the request goes directly to that contractor.
 */
const AIContractorHiringDialog = ({ open, onClose, onComplete, contractorId = null, projectId = null, milestoneId = null, milestoneName = null }) => {
  const { getApiClient, user } = useAuth();

  // ── Wizard navigation ──────────────────────────────────────────────────────
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [improving, setImproving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ── Contractor selection (post-questions) ──────────────────────────────────
  const [matchedContractors, setMatchedContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [showContractorSelection, setShowContractorSelection] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // ── Filter state for contractor selection grid ─────────────────────────────
  const [filterSearch, setFilterSearch] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showPreviousOnly, setShowPreviousOnly] = useState(false);
  const [previousContractors, setPreviousContractors] = useState([]);

  // ── Step 0: Project Association ────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showProjectWizard, setShowProjectWizard] = useState(false);

  // ── Step 1: Scope Selection ────────────────────────────────────────────────
  const [scopeType, setScopeType] = useState('entire'); // 'entire' | 'stage' | 'task'
  const [scopeDescription, setScopeDescription] = useState('');
  const [selectedStageId, setSelectedStageId] = useState('');
  const [projectMilestones, setProjectMilestones] = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [activeMilestoneContext, setActiveMilestoneContext] = useState(null); // { id, name, description }

  // ── Questions for steps 2-4 (timeline, budget, description) ───────────────
  const questions = [
    {
      id: 'timeline',
      text: 'When do you need this work completed?',
      placeholder: 'e.g., Within 1 week, In 2 months, Flexible...',
      examples: ['Urgent (within 1 week)', 'Within 1 month', 'In 2-3 months', 'Flexible']
    },
    {
      id: 'description',
      text: 'Any additional details for the contractor? (specific materials, finishes, access requirements, etc.)',
      placeholder: 'e.g., Complete kitchen remodel including cabinets, countertops, plumbing, electrical...',
      multiline: true,
      rows: 4,
      allowAI: true,
      allowFiles: true,
      optional: true
    }
  ];

  // currentQuestion is only valid for steps 2-4
  const currentQuestion = step >= 2 ? questions[step - 2] : null;
  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  // ── Fetch projects when the dialog opens ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    fetchProjects();
    if (contractorId) {
      setSelectedContractors([contractorId]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When a project is selected, pre-fill scope description & fetch milestones
  useEffect(() => {
    if (!selectedProject) return;
    if (scopeType === 'entire') {
      setScopeDescription(selectedProject.description || '');
    }
    // Fetch milestones for this project
    const fetchMilestones = async () => {
      setMilestonesLoading(true);
      try {
        const apiClient = getApiClient();
        const response = await apiClient.get(`/projects/${selectedProject.id}/milestones`);
        const raw = response.data;
        const list = Array.isArray(raw) ? raw : (raw?.data || []);
        console.log('[AIContractorHiringDialog] Fetched milestones for project', selectedProject.id, ':', list.length, 'milestones', list.map(m => m.milestoneDetails?.name || m.name || m.title));
        setProjectMilestones(list);

        // If milestoneId prop was passed, auto-select it
        if (milestoneId && list.length > 0) {
          const match = list.find(m => m.id === milestoneId || String(m.id) === String(milestoneId));
          if (match) {
            const msName = match.title || match.name || match.milestoneDetails?.name || milestoneName || '';
            const msDesc = match.description || match.milestoneDetails?.description || '';
            setScopeType('stage');
            setSelectedStageId(`milestone_${match.id}`);
            setScopeDescription(msDesc || msName);
            setActiveMilestoneContext({ id: match.id, name: msName, description: msDesc });
          } else {
            console.warn('[AIContractorHiringDialog] milestoneId prop', milestoneId, 'not found in fetched list. IDs:', list.map(m => m.id));
          }
        }
      } catch (err) {
        console.error('[AIContractorHiringDialog] Error fetching milestones for project', selectedProject.id, ':', err.response?.status, err.message);
        setProjectMilestones([]);
      } finally {
        setMilestonesLoading(false);
      }
    };
    fetchMilestones();
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ────────────────────────────────────────────────────────────────
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const buildProjectContext = () => {
    if (!selectedProject) return '';
    let context = `Project: "${selectedProject.title}", Location: "${selectedProject.location}"`;
    if (activeMilestoneContext) {
      context += ` | Milestone: "${activeMilestoneContext.name}"`;
      if (activeMilestoneContext.description) {
        context += ` — ${activeMilestoneContext.description}`;
      }
    } else if (milestoneName) {
      context += ` | Milestone: "${milestoneName}"`;
    }
    return context;
  };

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get('/projects');
      const list = response.data.data?.projects || response.data.data || [];
      setProjects(list);
      // Auto-select project from prop (milestone flow carries projectId via URL params)
      if (projectId && list.length > 0) {
        const match = list.find(p => String(p.id) === String(projectId));
        if (match) {
          setSelectedProject(match);
          if (step === 0) setStep(1);  // Skip "Which project?" step
        }
      }
      // Fallback: auto-select if only one project
      else if (list.length === 1) {
        setSelectedProject(list[0]);
        if (step === 0) setStep(1);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  // ── Improve with AI ────────────────────────────────────────────────────────
  // text    — current content of the field being improved
  // setText — state setter for that field
  const handleImproveWithAI = async (text, setText) => {
    if (!text.trim()) {
      showSnackbar('Please enter some content first', 'warning');
      return;
    }
    setImproving(true);
    try {
      const apiClient = getApiClient();
      const milestoneCtx = activeMilestoneContext
        ? `${activeMilestoneContext.name}${activeMilestoneContext.description ? ' — ' + activeMilestoneContext.description : ''}`
        : milestoneName || null;

      const response = await apiClient.post('/bids/ai/improve-section', {
        section: step === 1 ? 'scope_description' : 'project_description',
        currentContent: text,
        improvementRequest: buildProjectContext(),
        milestoneContext: milestoneCtx
      });
      setText(response.data.data.improvedContent);
      showSnackbar('✨ Content improved!', 'success');
    } catch (error) {
      console.error('Error improving content:', error);
      const msg = error.response?.data?.error || error.message || 'Failed to improve content';
      showSnackbar(msg, 'error');
    } finally {
      setImproving(false);
    }
  };

  // ── Scope type change ──────────────────────────────────────────────────────
  const handleScopeTypeChange = (newType) => {
    setScopeType(newType);
    setSelectedStageId('');
    setActiveMilestoneContext(null);
    if (newType === 'entire' && selectedProject) {
      setScopeDescription(selectedProject.description || '');
    } else if (newType === 'task' && milestoneName) {
      // Pre-fill specific task with milestone info so user doesn't re-type
      const msMatch = projectMilestones.find(m => m.id === milestoneId || String(m.id) === String(milestoneId));
      const msDesc = msMatch?.description || msMatch?.milestoneDetails?.description || '';
      setScopeDescription(msDesc || milestoneName);
    } else {
      setScopeDescription('');
    }
  };

  // ── Construction stage selected ────────────────────────────────────────────
  const handleStageChange = (stageId) => {
    setSelectedStageId(stageId);

    // Check if this is a milestone selection (prefixed with "milestone_")
    if (stageId.startsWith('milestone_')) {
      const msId = stageId.replace('milestone_', '');
      const ms = projectMilestones.find(m => String(m.id) === msId);
      if (ms) {
        const msName = ms.title || ms.name || ms.milestoneDetails?.name || '';
        const msDesc = ms.description || ms.milestoneDetails?.description || '';
        setScopeDescription(msDesc || msName);
        setActiveMilestoneContext({ id: ms.id, name: msName, description: msDesc });
        return;
      }
    }

    // Standard construction stage
    setActiveMilestoneContext(null);
    const stage = CONSTRUCTION_STAGES.find(s => s.id === stageId);
    setScopeDescription(stage ? stage.suggestion : '');
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length + uploadedFiles.length > 10) {
      showSnackbar('Maximum 10 files allowed', 'warning');
      return;
    }
    setUploadedFiles([...uploadedFiles, ...files]);
    showSnackbar(`${files.length} file(s) added`, 'success');
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  // ── Contractor selection ───────────────────────────────────────────────────
  const handleToggleContractor = (contractorId) => {
    setSelectedContractors(prev =>
      prev.includes(contractorId)
        ? prev.filter(id => id !== contractorId)
        : [...prev, contractorId]
    );
  };

  const filteredContractors = matchedContractors.filter(contractor => {
    const name = (contractor.full_name || contractor.name || '').toLowerCase();
    const location = (contractor.city || contractor.location || '').toLowerCase();
    const type = (contractor.contractor_type || contractor.type || '').toLowerCase();
    const cId = contractor.id || contractor.user_id;

    const matchesSearch = !filterSearch || name.includes(filterSearch.toLowerCase());
    const matchesLocation = !filterLocation || location.includes(filterLocation.toLowerCase());
    const matchesType = !filterType || type.includes(filterType.toLowerCase());
    const matchesPrevious = !showPreviousOnly || previousContractors.includes(cId);

    return matchesSearch && matchesLocation && matchesType && matchesPrevious;
  });

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleNext = async () => {
    setError(null);

    // ── Step 0: Project Association ──────────────────────────────────────────
    if (step === 0) {
      if (!selectedProject) {
        setError('Please select a project to continue.');
        return;
      }
      setAiTyping(true);
      await new Promise(r => setTimeout(r, 500));
      setAiTyping(false);
      setStep(1);
      return;
    }

    // ── Step 1: Scope Selection ───────────────────────────────────────────────
    if (step === 1) {
      if (!scopeDescription.trim()) {
        setError('Please describe the scope of work to continue.');
        return;
      }
      if (scopeType === 'stage' && !selectedStageId) {
        setError('Please select a construction stage to continue.');
        return;
      }
      // Commit scope + location into answers so the backend payload is correct
      const newAnswers = {
        ...answers,
        workType: scopeDescription.trim(),
        location: selectedProject?.location || ''
      };
      setAnswers(newAnswers);
      setAiTyping(true);
      await new Promise(r => setTimeout(r, 500));
      setAiTyping(false);
      setStep(2);
      return;
    }

    // ── Steps 2-4: timeline / budget / description ────────────────────────────
    if (!currentAnswer.trim() && !currentQuestion.optional) {
      setError('Please provide an answer to continue.');
      return;
    }

    const newAnswers = {
      ...answers,
      [currentQuestion.id]: currentAnswer.trim()
    };
    setAnswers(newAnswers);
    setCurrentAnswer('');
    setError(null);

    setAiTyping(true);
    await new Promise(r => setTimeout(r, 800));
    setAiTyping(false);

    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1);
    } else {
      // Last step completed
      if (contractorId) {
        // Pre-seeded contractor — skip contractor selection
        setSelectedContractors([contractorId]);
        setShowPreview(true);
      } else if (matchedContractors.length > 0) {
        setShowPreview(true);
      } else {
        await handleSubmit(newAnswers);
      }
    }
  };

  const handleBack = () => {
    if (step === 0) return;
    if (step <= 2) {
      setStep(step - 1);
      setCurrentAnswer('');
      return;
    }
    // Step 3: restore the previous question's answer into the text field
    const prevQuestion = questions[step - 3]; // step=3→questions[0] (timeline)
    setStep(step - 1);
    setCurrentAnswer(answers[prevQuestion?.id] || '');
  };

  const handleExampleClick = (example) => {
    setCurrentAnswer(example);
  };

  // ── Fetch contractors after all questions are answered ─────────────────────
  const handleSubmit = async (finalAnswers) => {
    setIsLoading(true);
    setError(null);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get('/contractors');
      const contractors = response.data.data || response.data || [];

      if (contractors.length === 0) {
        setError('No contractors available at the moment. Please try again later.');
        setIsLoading(false);
        return;
      }

      try {
        const historyResponse = await apiClient.get('/hires/contractor-history');
        const history = historyResponse.data.data || historyResponse.data || [];
        setPreviousContractors(history.map(c => c.contractor_id || c.id));
      } catch {
        setPreviousContractors([]);
      }

      setMatchedContractors(contractors);
      setSelectedContractors([]);
      setShowPreview(true);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading contractors:', err);
      setError(err.response?.data?.message || 'Failed to load contractors. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSendRequests = () => {
    setShowPreview(false);
    if (contractorId) {
      // Pre-seeded: go straight to submit, bypassing contractor selection UI
      handleSubmitDocument();
    } else {
      setShowContractorSelection(true);
    }
  };

  const handleSubmitDocument = async () => {
    if (selectedContractors.length === 0) {
      showSnackbar('Please select at least one contractor', 'warning');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiClient = getApiClient();
      // Use milestone from dropdown selection if available, otherwise fall back to props
      const effectiveMilestoneId = activeMilestoneContext?.id || milestoneId || null;
      const effectiveMilestoneName = activeMilestoneContext?.name || milestoneName || null;

      const quoteRequests = selectedContractors.map(cId =>
        apiClient.post('/quote-requests', {
          contractorId: cId,
          projectId: selectedProject?.id || null,
          milestoneId: effectiveMilestoneId,
          milestoneName: effectiveMilestoneName,
          projectDescription: effectiveMilestoneName
            ? `[Milestone: ${effectiveMilestoneName}] ${answers.description || answers.workType}`
            : (answers.description || answers.workType),
          projectLocation: answers.location || selectedProject?.location || '',
          timeline: answers.timeline,
          additionalNotes: answers.workType,
          clientName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.company_name || null,
          files: uploadedFiles
        })
      );

      await Promise.all(quoteRequests);

      setSuccess(true);
      showSnackbar(`Quote requests sent to ${selectedContractors.length} contractor${selectedContractors.length > 1 ? 's' : ''}!`, 'success');

      setTimeout(() => {
        if (onComplete) {
          onComplete({
            contractors: matchedContractors.filter(c => selectedContractors.includes(c.id)),
            answers
          });
        }
        handleClose();
      }, 3000);
    } catch (err) {
      console.error('Error sending quote requests:', err);
      setError(err.response?.data?.message || 'Failed to send requests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset & close ──────────────────────────────────────────────────────────
  const handleClose = () => {
    if (isLoading) return;
    setStep(0);
    setAnswers({});
    setCurrentAnswer('');
    setError(null);
    setSuccess(false);
    setMatchedContractors([]);
    setSelectedContractors([]);
    setShowContractorSelection(false);
    setShowPreview(false);
    setUploadedFiles([]);
    // project / scope state
    setProjects([]);
    setSelectedProject(null);
    setProjectsLoading(false);
    setScopeType('entire');
    setScopeDescription('');
    setSelectedStageId('');
    onClose();
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, minHeight: '600px', maxHeight: '90vh' }
      }}
    >
      <DialogCloseButton onClick={handleClose} sx={{ color: 'white' }} />
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          background: brand.gradient,
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Construction sx={{ fontSize: 32 }} />
          <Box flexGrow={1}>
            <Typography variant="h5" fontWeight={700}>
              Request Contractor Quotes
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {milestoneName
                ? `Find a contractor for: ${milestoneName}`
                : 'Describe your project and receive quotes from qualified contractors'}
            </Typography>
          </Box>
        </Box>

        {!showContractorSelection && !success && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': { backgroundColor: 'white', borderRadius: 3 }
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.9 }}>
              Step {step + 1} of {TOTAL_STEPS}
            </Typography>
          </Box>
        )}
        {showContractorSelection && !success && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Select contractors to send your quote request to
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* ── Success ── */}
        {success && (
          <Fade in={success}>
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>🎉 Quote request sent!</Typography>
              <Typography variant="body2">
                Your project details have been sent to {selectedContractors.length} contractor{selectedContractors.length > 1 ? 's' : ''}.
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Expect to receive quotes within 24–48 hours.
              </Typography>
            </Alert>
          </Fade>
        )}

        {/* ── Error ── */}
        {error && !success && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* ════════════════════════════════════════════════════════════
            Contractor Selection Grid (post-questions)
        ════════════════════════════════════════════════════════════ */}
        {showContractorSelection && !success && (
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Choose Who Receives Your Quote Request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the contractors you'd like to send your quote request to.
            </Typography>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth size="small" placeholder="Search by name..."
                    value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)}
                    InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>🔍</Box> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth size="small" placeholder="Location..."
                    value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}
                    InputProps={{ startAdornment: <LocationOn sx={{ mr: 0.5, color: 'text.secondary', fontSize: 20 }} /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth size="small" placeholder="Type (e.g. Plumber)..."
                    value={filterType} onChange={(e) => setFilterType(e.target.value)}
                    InputProps={{ startAdornment: <Construction sx={{ mr: 0.5, color: 'text.secondary', fontSize: 20 }} /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPreviousOnly}
                        onChange={(e) => setShowPreviousOnly(e.target.checked)}
                        size="small" color="success"
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>Worked with before</Typography>}
                    sx={{ ml: 0.5 }}
                  />
                </Grid>
              </Grid>
              {showPreviousOnly && previousContractors.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  You haven't worked with any contractors yet
                </Typography>
              )}
            </Paper>

            <Paper elevation={0} sx={{ backgroundColor: '#ede7f6', border: `1px solid ${brand.primary}`, borderRadius: 2, p: 2, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" fontWeight={600} color={brand.primary}>
                  {selectedContractors.length} contractor{selectedContractors.length !== 1 ? 's' : ''} selected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredContractors.length} of {matchedContractors.length}
                </Typography>
              </Box>
            </Paper>

            <Grid container spacing={2}>
              {filteredContractors.map((contractor) => (
                <Grid item xs={12} sm={6} key={contractor.id}>
                  <Card
                    sx={{
                      border: selectedContractors.includes(contractor.id) ? `2px solid ${brand.primary}` : `1px solid ${border.default}`,
                      borderRadius: 2, cursor: 'pointer', transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': { boxShadow: 3, borderColor: brand.primary }
                    }}
                    onClick={() => handleToggleContractor(contractor.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <Checkbox
                          checked={selectedContractors.includes(contractor.id)}
                          onChange={() => handleToggleContractor(contractor.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mt: -1, ml: -1 }}
                        />
                        <Avatar
                          src={contractor.profile_image_url || contractor.avatar}
                          alt={contractor.name || contractor.full_name}
                          sx={{ width: 48, height: 48 }}
                        >
                          {(contractor.name || contractor.full_name)?.charAt(0)}
                        </Avatar>
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {contractor.full_name || contractor.name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                            {contractor.tier && (
                              <Chip label={contractor.tier.toUpperCase()} size="small"
                                sx={{ backgroundColor: brand.primary, color: 'white', fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
                            )}
                            {contractor.contractor_type && (
                              <Chip label={contractor.contractor_type} size="small"
                                sx={{ backgroundColor: '#ede7f6', color: brand.primaryDark, fontSize: '0.65rem', height: 18 }} />
                            )}
                            {previousContractors.includes(contractor.id || contractor.user_id) && (
                              <Chip label="Worked With" size="small"
                                sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 18, fontWeight: 600 }} />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                            {(contractor.avg_rating > 0 || contractor.rating > 0) && (
                              <Box display="flex" alignItems="center">
                                <Star sx={{ fontSize: 16, color: '#ffc107', mr: 0.3 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {parseFloat(contractor.avg_rating || contractor.rating).toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                            {(contractor.base_city || contractor.location) && (
                              <Box display="flex" alignItems="center">
                                <LocationOn sx={{ fontSize: 16, color: '#666', mr: 0.3 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {contractor.base_city || contractor.location}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════════
            Questions (steps 0-4)
        ════════════════════════════════════════════════════════════ */}
        {!success && !showContractorSelection && (
          <>
            {/* ── Step 0: Project Association ─────────────────────────────── */}
            {step === 0 && (
              <Paper
                elevation={0}
                sx={{ backgroundColor: '#f5f5f5', boxShadow: `inset 0 0 0 2px ${brand.primary}`, borderRadius: 2, p: 3, mb: 3, position: 'relative' }}
              >
                {/* AI bubble */}
                <Box sx={{
                  position: 'absolute', top: -20, left: 20, zIndex: 1,
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: brand.primary, border: '3px solid white', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', boxShadow: 3
                }}>
                  <FolderOpen sx={{ color: 'white', fontSize: 22 }} />
                </Box>

                <Typography variant="h6" sx={{ mt: 1, mb: 2, color: brand.primaryDark }}>
                  Which project is this quote for?
                </Typography>

                {projectsLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading your projects…</Typography>
                  </Box>
                )}

                {!projectsLoading && projects.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      You don't have any projects yet.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowProjectWizard(true)}
                    >
                      Create your first project →
                    </Button>
                  </Alert>
                )}

                {!projectsLoading && projects.length > 1 && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select project</InputLabel>
                    <Select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const p = projects.find(pr => pr.id === e.target.value);
                        setSelectedProject(p || null);
                        setScopeType('entire');
                        setScopeDescription(p?.description || '');
                        setSelectedStageId('');
                      }}
                      label="Select project"
                    >
                      {projects.map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Project confirmation card */}
                {selectedProject && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2, mt: 1,
                      backgroundColor: '#ede7f6',
                      border: '1px solid #b39ddb',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {selectedProject.title}
                    </Typography>
                    {selectedProject.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedProject.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: brand.primary }} />
                      <Typography variant="body2" color={brand.primary} fontWeight={500}>
                        {selectedProject.location}
                      </Typography>
                    </Box>
                    {projects.length === 1 && (
                      <Chip label="Auto-associated" size="small" sx={{ mt: 1, bgcolor: '#d1c4e9', color: '#4527a0', fontSize: '0.7rem' }} />
                    )}
                  </Paper>
                )}
              </Paper>
            )}

            {/* ── Step 1: Scope Selection ──────────────────────────────────── */}
            {step === 1 && (
              <Paper
                elevation={0}
                sx={{ backgroundColor: '#f5f5f5', boxShadow: `inset 0 0 0 2px ${brand.primary}`, borderRadius: 2, p: 3, mb: 3, position: 'relative' }}
              >
                <Box sx={{
                  position: 'absolute', top: -20, left: 20, zIndex: 1,
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: brand.primary, border: '3px solid white', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', boxShadow: 3
                }}>
                  <Assignment sx={{ color: 'white', fontSize: 22 }} />
                </Box>

                <Typography variant="h6" sx={{ mt: 1, mb: 2, color: brand.primaryDark }}>
                  What do you need a quote for?
                </Typography>

                {/* Simplified view when milestone was pre-selected from project page */}
                {milestoneId && activeMilestoneContext ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} color={brand.primaryDark}>
                        {activeMilestoneContext.name}
                      </Typography>
                    </Box>
                    {activeMilestoneContext.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                        {activeMilestoneContext.description}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      sx={{ mt: 1, ml: 2.5, textTransform: 'none', fontSize: '0.75rem' }}
                      onClick={() => {
                        // Allow user to change scope if needed
                        setActiveMilestoneContext(null);
                      }}
                    >
                      Change scope
                    </Button>
                  </Paper>
                ) : (
                <RadioGroup value={scopeType} onChange={(e) => handleScopeTypeChange(e.target.value)}>
                  <FormControlLabel value="entire" control={<Radio />} label="Entire Project" />
                  <FormControlLabel value="stage" control={<Radio />} label="A Stage" />
                  <FormControlLabel value="task" control={<Radio />} label="A specific task" />
                </RadioGroup>
                )}

                {/* Construction stage dropdown — hidden when milestone is pre-confirmed */}
                {scopeType === 'stage' && !(milestoneId && activeMilestoneContext) && (
                  <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                    <InputLabel>Select a stage</InputLabel>
                    <Select
                      value={selectedStageId}
                      onChange={(e) => handleStageChange(e.target.value)}
                      label="Select a stage"
                    >
                      {milestonesLoading && (
                        <MenuItem disabled key="loading-ms">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">Loading your milestones...</Typography>
                          </Box>
                        </MenuItem>
                      )}
                      {!milestonesLoading && projectMilestones.length > 0 && [
                        <ListSubheader key="ms-header" sx={{ bgcolor: '#e8f5e9', fontWeight: 700, color: brand.primaryDark }}>
                          Your Milestones
                        </ListSubheader>,
                        ...projectMilestones.map(ms => {
                          const msName = ms.title || ms.name || ms.milestoneDetails?.name || 'Untitled';
                          const msDesc = ms.description || ms.milestoneDetails?.description || '';
                          return (
                            <MenuItem key={`milestone_${ms.id}`} value={`milestone_${ms.id}`}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>{msName}</Typography>
                                {msDesc && (
                                  <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 400 }}>
                                    {msDesc}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          );
                        })
                      ]}
                      {!milestonesLoading && projectMilestones.length === 0 && (
                        <MenuItem disabled key="no-ms-hint" sx={{ whiteSpace: 'normal', opacity: '1 !important' }}>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="body2" color="warning.main" fontWeight={600}>
                              Tip: Create milestones in your project first
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Milestones let you hire contractors for specific tasks (e.g. "Painting") instead of picking from generic stages below.
                            </Typography>
                          </Box>
                        </MenuItem>
                      )}
                      <ListSubheader key="std-header" sx={{ bgcolor: '#f5f5f5', fontWeight: 700 }}>
                        Standard Construction Stages
                      </ListSubheader>
                      {CONSTRUCTION_STAGES.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {s.purpose}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Description textarea — shown when scope is clear */}
                {(scopeType !== 'stage' || selectedStageId) && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={scopeDescription}
                      onChange={(e) => setScopeDescription(e.target.value)}
                      disabled={improving}
                      placeholder={
                        scopeType === 'entire'
                          ? 'Describe the full project scope (auto-filled from your project, edit as needed)…'
                          : scopeType === 'stage'
                          ? 'Describe what this stage requires in detail…'
                          : 'Describe the specific task you need quoted…'
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          fontSize: '16px',
                          lineHeight: 1.6
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small"
                        startIcon={improving ? <CircularProgress size={16} /> : <AutoAwesome />}
                        onClick={() => handleImproveWithAI(scopeDescription, setScopeDescription)}
                        disabled={!scopeDescription.trim() || improving}
                      >
                        {improving ? 'Improving…' : 'Improve with AI'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* ── Steps 2-4: timeline / budget / description ───────────────── */}
            {step >= 2 && currentQuestion && (
              <Paper
                elevation={0}
                sx={{ backgroundColor: '#f5f5f5', boxShadow: `inset 0 0 0 2px ${brand.primary}`, borderRadius: 2, p: 3, mb: 3 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '50%',
                    backgroundColor: brand.primary, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <AutoAwesome sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: brand.primaryDark }}>
                    {currentQuestion.text}
                  </Typography>
                </Box>

                {currentQuestion.examples && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Quick select:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {currentQuestion.examples.map((example, idx) => (
                        <Chip
                          key={idx} label={example}
                          onClick={() => handleExampleClick(example)}
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#ede7f6', borderColor: brand.primary } }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={currentQuestion.rows || 2}
                  maxRows={6}
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  disabled={isLoading || aiTyping || improving}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !currentQuestion.multiline && !isLoading) {
                      e.preventDefault();
                      handleNext();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      fontSize: '16px',
                      lineHeight: 1.6
                    }
                  }}
                />

                {/* Improve with AI — description step */}
                {currentQuestion.allowAI && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={improving ? <CircularProgress size={16} /> : <AutoAwesome />}
                      onClick={() => handleImproveWithAI(currentAnswer, setCurrentAnswer)}
                      disabled={!currentAnswer.trim() || improving || isLoading}
                    >
                      {improving ? 'Improving…' : 'Improve with AI'}
                    </Button>
                  </Box>
                )}

                {/* File upload — description step */}
                {currentQuestion.allowFiles && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFile />}
                      size="small"
                      disabled={isLoading}
                    >
                      Upload Photos
                      <input type="file" hidden multiple accept="image/*" onChange={handleFileUpload} />
                    </Button>
                    {uploadedFiles.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        {uploadedFiles.map((file, index) => (
                          <Chip
                            key={index}
                            icon={<Image />}
                            label={file.name.length > 20 ? file.name.substring(0, 20) + '…' : file.name}
                            onDelete={() => handleRemoveFile(index)}
                            size="small"
                            sx={{ maxWidth: 200 }}
                          />
                        ))}
                      </Stack>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {uploadedFiles.length}/10 files uploaded
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {/* AI typing indicator */}
            {aiTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Processing…</Typography>
              </Box>
            )}

            {/* Previous answers summary */}
            {step > 0 && Object.keys(answers).length > 0 && (
              <Paper elevation={0} sx={{ backgroundColor: '#f9f9f9', border: `1px solid ${border.default}`, borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Your Answers:
                </Typography>
                {Object.entries(answers).map(([key, value]) => (
                  <Typography key={key} variant="body2" color="text.secondary">
                    <strong>{ANSWER_LABELS[key] || key}:</strong> {value}
                  </Typography>
                ))}
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      {/* ── Dialog Actions ── */}
      {!success && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${border.default}` }}>
          {showContractorSelection ? (
            <>
              <Button
                onClick={() => { setShowContractorSelection(false); setShowPreview(true); }}
                disabled={isLoading}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back to Preview
              </Button>
              <Box flexGrow={1} />
              <Button onClick={handleClose} disabled={isLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSubmitDocument}
                disabled={isLoading || selectedContractors.length === 0}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  background: brand.gradient,
                  color: 'white', textTransform: 'none', fontWeight: 600, px: 3,
                  '&:hover': { background: brand.gradientHover }
                }}
              >
                Send Quote Requests ({selectedContractors.length})
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleBack}
                disabled={step === 0 || isLoading}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
              <Box flexGrow={1} />
              <Button onClick={handleClose} disabled={isLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  isLoading || aiTyping ||
                  (step === 0 && (projectsLoading || projects.length === 0 || !selectedProject)) ||
                  (step === 1 && (!scopeDescription.trim() || (scopeType === 'stage' && !selectedStageId))) ||
                  (step >= 2 && !currentAnswer.trim() && !currentQuestion?.optional)
                }
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  background: brand.gradient,
                  color: 'white', textTransform: 'none', fontWeight: 600, px: 3,
                  '&:hover': { background: brand.gradientHover }
                }}
              >
                {step === TOTAL_STEPS - 1 ? 'Preview & Send' : 'Next'}
              </Button>
            </>
          )}
        </DialogActions>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Document Preview Modal ── */}
      {showPreview && (
        <ContractorHireDocumentPreview
          open={showPreview}
          onClose={() => {
            setShowPreview(false);
            setStep(TOTAL_STEPS - 1);
            setCurrentAnswer(answers[questions[questions.length - 1].id] || '');
          }}
          onSubmit={handleSendRequests}
          data={{
            projectTitle: selectedProject ? selectedProject.title : (answers.workType || 'Project'),
            clientName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.company_name || 'Client',
            workType: answers.workType,
            location: answers.location || selectedProject?.location,
            projectSummary: answers.description,
            detailedDescription: answers.description,
            keyRequirements: answers.description,
            qualityStandards: 'Industry standard quality and workmanship',
            startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            duration: answers.timeline,
            milestones: 'To be determined with selected contractor',
            timelineUrgency: answers.timeline?.toLowerCase().includes('urgent') ? 'Urgent' : 'Standard',
            numberOfContractors: selectedContractors.length,
            evaluationCriteria: '1. Experience and qualifications\n2. Proposed timeline and approach\n3. Cost and value\n4. References and past work\n5. Communication and professionalism',
            requiredQualifications: 'Licensed and insured contractor with relevant experience',
            documentsProvided: uploadedFiles.length > 0 ? `${uploadedFiles.length} file(s) attached` : 'None',
            uploadedFiles: uploadedFiles,
            additionalInfo: 'Additional details available upon request',
            termsAndConditions: `By submitting a proposal, contractors acknowledge and agree to:\n\n• All information provided is accurate and complete\n• They have the necessary licenses, permits, and insurance to perform the work\n• They will maintain confidentiality of all project information\n• The client reserves the right to accept or reject any proposal\n• Award of contract does not guarantee exclusivity unless specified\n• All work will comply with local building codes and regulations`,
            responseDeadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
          }}
        />
      )}
    </Dialog>

      <UnifiedProjectWizard
        open={showProjectWizard}
        onClose={() => setShowProjectWizard(false)}
        onComplete={() => {
          setShowProjectWizard(false);
          fetchProjects();
        }}
      />
    </>
  );
};

export default AIContractorHiringDialog;
